import { browserHistory } from "react-router"

import jsonFetch from "common/services/fetch/jsonFetch"
import authStore from "common/services/user/authStore"
import convertPrice from "common/services/price/convertPrice"
import createSearchParams from "common/services/search/createSearchParams"
import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import { HEADER_USER_TOKEN } from "mmb/services/bookings/bookingsApi"

const apiUrl = () => window.reduxStore.getState().options.get("priceAlertUrl")

function parsePrice(price) {
  return parseFloat(price.replace(/[٫,]/, "."))
}

export function makePayload({ state, language, currency, searchForm, filters }) {
  const searchParams = createSearchParams({
    origin: searchForm.origin,
    destination: searchForm.destination,
    outboundDate: searchForm.outboundDate,
    inboundDate: searchForm.inboundDate,
    passengers: searchForm.passengers,
    filters,
  })

  const options = {
    locale: language,
    currency,
    max_price: parsePrice(state.price),
  }

  return JSON.stringify({ ...searchParams, ...options })
}

function createSearchPayload(searchForm, filters, options) {
  let searchParams = null
  const payload = []

  if (searchForm.getMode() === "multicity") {
    searchForm.multicity.forEach(multicityData => {
      searchParams = createSearchParams({
        origin: multicityData.origin,
        destination: multicityData.destination,
        outboundDate: multicityData.outboundDate,
        passengers: searchForm.passengers,
        filters,
        oneForCity: multicityData.destination.isLarge(),
      })
      if (!searchForm.addingAnywhereToMulticity()) {
        Object.assign(searchParams, { sort: filters.get("sortBy") })
      }
      payload.push({ ...searchParams, ...options })
    })
  } else {
    searchParams = createSearchParams({
      origin: searchForm.origin,
      destination: searchForm.destination,
      outboundDate: searchForm.outboundDate,
      inboundDate: searchForm.inboundDate,
      passengers: searchForm.passengers,
      filters,
      oneForCity: searchForm.get("destination").isLarge(),
    })
    payload.push({ ...searchParams, ...options })
  }

  return payload
}

export function makeCheckPayload({ state, currency, searchForm, filters, brandId, user }) {
  const options = {
    currency,
    brand: brandId,
  }
  const searchPayload = createSearchPayload(searchForm, filters, options)
  const email = state.email || (user ? user.email : null)
  const authToken = user && user.email === state.email ? authStore.token : null

  return JSON.stringify({
    email,
    flights_api_v: "v3",
    search: searchPayload,
    auth_token: authToken,
  })
}

export function makeSubmitPayload({
  state,
  language,
  currency,
  currencies,
  searchForm,
  filters,
  user,
  brandId,
}) {
  const options = {
    currency,
    brand: brandId,
  }
  const searchPayload = createSearchPayload(searchForm, filters, options)

  const authToken = user && user.email === state.email ? authStore.token : null
  const email = state.email || (user ? user.email : null)

  return JSON.stringify({
    email,
    flights_api_v: "v3",
    search: searchPayload,
    url: browserHistory.getCurrentLocation().pathname + browserHistory.getCurrentLocation().search,
    locale: language,
    price: state.bestPrice ? convertPrice(state.bestPrice, currency, currencies) : null,
    currency,
    brand: brandId,
    auth_token: authToken,
  })
}

// TODO: auth_token should be send in authentication header not in query
const watchdogFetch = (url, options) => {
  const opts = options || {}
  opts.headers = {
    ...(opts.headers || {}),
    [HEADER_USER_TOKEN]: authStore.token ? authStore.token : "",
  }

  return jsonFetch(`${apiUrl()}/price-alerts/${url}`, options)
}

export const checkWatchdog = searchJson => watchdogFetch(`check`, { body: searchJson })

export const createWatchdog = searchJson =>
  watchdogFetch(`create`, { body: searchJson })
    .then(json => {
      cuckoo.track(cuckooEvents.SEARCH_WATCHDOG_CREATED, { searchJson })
      cuckoo.infinario("PriceAlert", { action: "submit", payload: searchJson })
      return json
    })
    .catch(err => {
      switch (err.status) {
        case 451:
          throw new Error("alreadyHasWatchdog")
        case 453:
          throw new Error("reached_limit")
        case 455:
          throw new Error("notLoggedIn")
        default:
          cuckoo.track(cuckooEvents.SEARCH_WATCHDOG_ERROR, { searchJson, err })
          throw new Error("generalError")
      }
    })

export const deactivateWatchdog = code =>
  watchdogFetch(`deactivate`, {
    body: {
      code,
    },
  })

export const userSearch = email =>
  watchdogFetch(`user-search`, {
    body: {
      email,
    },
  })

export const listWatchdogs = (lang, locale) =>
  watchdogFetch(`list`, {
    query: {
      lang,
      locale,
    },
  })

export const setActivityForWatchdog = (watchdog, active: boolean, locale) =>
  watchdogFetch(active ? "activate" : "deactivate", {
    query: {
      locale,
    },
    body: {
      code: watchdog.unsubscribe_hash,
    },
  })

export const updateWatchdog = (watchdog, interval, locale) =>
  watchdogFetch("update", {
    query: {
      locale,
      // Comment out - I hope token in body is good enough
      // token: authStore.token,
    },
    body: {
      code: watchdog.unsubscribe_hash,
      auth_token: authStore.token,
      interval,
    },
  })

export const removeWatchdog = (watchdog, locale) =>
  watchdogFetch("delete", {
    query: {
      locale,
    },
    body: {
      code: watchdog.unsubscribe_hash,
      auth_token: authStore.token,
    },
  })
